import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Header';
import { SEO, buildServiceSchema, buildBreadcrumbSchema } from '../components/SEO';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ServiceProviderCard } from '../components/service/ServiceProviderCard';
import { BookingPanel } from '../components/service/BookingPanel';

export default function ServiceDetail() {
  const { serviceId, serviceSlug } = useParams();
  const resolvedId = serviceSlug || serviceId;
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchService = useCallback(async () => {
    try {
      const response = await api.get(`/services/${resolvedId}`);
      setService(response.data);
      if (response.data.slug && serviceId && !serviceSlug && response.data.slug !== serviceId) {
        navigate(`/service/${response.data.slug}`, { replace: true });
      }
      api.post(`/recently-viewed/${response.data.service_id}`).catch(() => {});
    } catch (err) {
      console.error('Service fetch error:', err);
      navigate('/discover');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedId, serviceId, serviceSlug, navigate]);

  useEffect(() => { fetchService(); }, [fetchService]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-stone-200 rounded w-1/4 mb-4"></div>
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="h-10 bg-stone-200 rounded w-3/4"></div>
                <div className="h-6 bg-stone-200 rounded w-1/2"></div>
                <div className="h-24 bg-stone-200 rounded"></div>
              </div>
              <div className="space-y-4">
                <div className="h-10 bg-stone-200 rounded w-3/4"></div>
                <div className="h-6 bg-stone-200 rounded w-1/2"></div>
                <div className="h-24 bg-stone-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!service) return null;

  const breadcrumbItems = [
    { label: 'Discover', href: '/discover' },
    { label: service.category?.charAt(0).toUpperCase() + service.category?.slice(1), href: `/discover?category=${service.category}` },
    ...(service.provider?.brand_name ? [{ label: service.provider.brand_name, href: `/@${service.provider.username}` }] : []),
    { label: service.name },
  ];

  const jsonLd = [
    buildServiceSchema(service, service.provider),
    buildBreadcrumbSchema([
      { name: 'Home', url: window.location.origin },
      { name: 'Discover', url: `${window.location.origin}/discover` },
      { name: service.category, url: `${window.location.origin}/discover?category=${service.category}` },
      { name: service.name },
    ]),
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEO
        title={`${service.name}, ${service.provider?.brand_name || ''}`}
        description={`Book ${service.name} with ${service.provider?.brand_name || ''} in ${service.provider?.location || 'the UK'}. From £${service.price?.toFixed(0)}. ${service.description?.slice(0, 100) || ''}`}
        image={service.image}
        url={`${window.location.origin}/service/${service.slug || service.service_id}`}
        type="product"
        jsonLd={jsonLd}
      />
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Provider mini card, full reviews live on the provider profile
              (/@username) so this page stays action-focused. */}
          <div>
            <ServiceProviderCard provider={service.provider} />
          </div>

          {/* Booking Panel */}
          <BookingPanel service={service} user={user} />
        </div>
      </div>
    </div>
  );
}
